import { PlayerRef } from '@remotion/player';
import React, { useCallback, useEffect, useState } from 'react';
import { ToolButton } from '@crabs-ai/ds-react';
import { VolumeUpFill, VolumeOffFill } from '@crabs-ai/web-icons';
import { useTranslations } from 'next-intl';
import { isEventTargetInputElement } from '../utils/is-event-target-input-element';

export const MuteButton: React.FC<{
  playerRef: React.RefObject<PlayerRef | null>;
}> = ({ playerRef }) => {
  const t = useTranslations();
  const [muted, setMuted] = useState(playerRef.current?.isMuted() ?? false);

  const toggleMute = useCallback(() => {
    if (!playerRef.current) return;
    if (playerRef.current.isMuted()) {
      playerRef.current.unmute();
    } else {
      playerRef.current.mute();
    }
  }, [playerRef]);

  useEffect(() => {
    const { current } = playerRef;
    if (!current) return;

    const onMuteChange = () => {
      setMuted(current.isMuted());
    };

    current.addEventListener('mutechange', onMuteChange);
    return () => {
      current.removeEventListener('mutechange', onMuteChange);
    };
  }, [playerRef]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isEventTargetInputElement(e)) return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      if (e.code === 'KeyM') {
        e.preventDefault();
        toggleMute();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleMute]);

  return (
    <ToolButton
      size="sm"
      state={muted ? 'selected' : 'default'}
      onClick={toggleMute}
      isShowTooltip
      tooltipNode={
        muted
          ? t('Editor.timeline-controls.unmute')
          : t('Editor.timeline-controls.mute')
      }
      tooltipSubNode="[M]"
      tooltipPosition="top"
    >
      {muted ? <VolumeOffFill /> : <VolumeUpFill />}
    </ToolButton>
  );
};
